import { AgentEnriched } from '@/types/agent';
import { Action, Address, AddressV2, Link, YapMoney } from '@/types/general';

export enum CardsURI {
  Base = '/cards',
  BaseV2 = '/v2/cards',

  Blockings = '/blockings',
  Limits = '/limits',
  Freeze = '/freeze',
  Unfreeze = '/unfreeze',
  Pin = '/pin'
}

export enum DebitCardsURI {
  BaseV2 = '/v2/debit-cards',

  Order = '/order',

  Search = '/search',

  ReplaceBroken = '/replace/replace-broken',
  ReplaceStolen = '/replace/replace-stolen',
  RequestReidentVoucher = '/voucher/request'
}

export enum CardStatus {
  New = 'new',
  Funded = 'funded',
  Active = 'active',
  Frozen = 'frozen',
  Locked = 'locked',
  Blocked = 'blocked',
  Retired = 'retired',
  Deleted = 'deleted'
}

export enum CardType {
  Combo = 'combo',
  Virtual = 'virtual'
}

export enum CardDeliveryAddress {
  Company = 'company',
  Private = 'private'
}

export enum CardVisualType {
  Black = 'black',
  Abacus = 'abacus',
  AbacusExpense = 'abacusExpense',
  Traditional = 'traditional',
  Gold = 'gold',
  UrbanConnect = 'urbanConnect',
  Swibeco = 'swibeco'
}

export enum CardVisualName {
  Frozen = 'card_frozen',
  Black = 'card_black',
  BlackVirtual = 'card_black_virtual',
  Abacus = 'card_abacus',
  AbacusVirtual = 'card_abacus_virtual',
  AbacusExpense = 'card_abacus_expense',
  AbacusExpenseVirtual = 'card_abacus_expense_virtual',
  Traditional = 'card_traditional',
  Gold = 'card_gold',
  UrbanConnect = 'card_urbanConnect',
  Swibeco = 'card_swibeco'
}

export const getCardVisualTypeAsset = (visualType: CardVisualType): string => {
  switch (visualType) {
    case CardVisualType.Abacus:
      return '/images/cards/card_abacus.png';
    case CardVisualType.Black:
      return '/images/cards/card_black.png';
    case CardVisualType.Gold:
      return '/images/cards/card_gold.png';
    case CardVisualType.AbacusExpense:
      return '/images/cards/card_abacusExpense.png';
    case CardVisualType.UrbanConnect:
      return '/images/cards/card_urbanConnect.png';
    case CardVisualType.Swibeco:
      return '/images/cards/card_swibeco.png';
    default:
      return '/images/cards/card_traditional.png';
  }
};

export enum CardActionName {
  ViewCardLimit = 'ViewCardLimit',
  ViewTransactionFilters = 'ViewTransactionFilters',
  AddToAppleWallet = 'AddToAppleWallet',
  AddToGoogleWallet = 'AddToGoogleWallet',
  FreezeCard = 'FreezeCard',
  UnfreezeCard = 'UnfreezeCard',
  RenewCard = 'RenewCard', // Was "report broken" in v1
  ReplaceLostStolenCard = 'ReplaceLostStolenCard',
  ReplaceExpiringCard = 'ReplaceExpiringCard',
  SetCardLimit = 'SetCardLimit',
  SetCardPin = 'SetCardPin',
  ViewCardPin = 'ViewCardPin',
  AddMCCBlocking = 'AddMCCBlocking',
  CreateTransactionFilters = 'CreateTransactionFilters',
  DeleteCard = 'DeleteCard',
  RequestCardReidentVoucher = 'RequestCardReidentVoucher', // Was voucher in v1
  ViewDigitalWalletTokens = 'ViewDigitalWalletTokens' // To be provided by BE at some point - Was "digitalWallets" in v1
}

interface BaseCardDetails {
  cardName: string;
  cardUID: string;
  customerType: 'business' | 'private';
  deliveryAddress: AddressV2;
  holderEnriched?: AgentEnriched;
  limit: YapMoney;
  line1: string;
  line2: string;
  note: string;
  ownerEnriched: AgentEnriched;
  panLastFour: string;
  pendingDelivery: boolean;
  productType: 'combo' | 'virtual';
  productVisual: 'abacus' | 'abacusExpense' | 'black' | 'gold' | 'swibeco' | 'traditional' | 'urbanConnect';
  status: CardStatus;
  virtualIdentityUid: string;
}
interface EncryptedSecureCardDetails {
  encCVV: string;
  encExpiry: string;
  encPAN: string;
  nonce: number[];
  pinState: string;
}

interface CardOrderInfo {
  operation: 'freeze' | 'lock' | 'unfreeze' | 'view-pin';
  orderUid: string;
  status: 'accepted' | 'executed' | 'new' | 'rejected' | 'retired';
}
interface InternalCardDetails {
  accountUid: string;
  createdAt: string;
  holderUid: string;
  ongoingOrders: CardOrderInfo[];
  ownerUid: string;
  processorCardId: string;
  updatedAt: string;
}
interface SecureCardDetails {
  cvv?: string;
  expiry?: string;
  pan?: string;
  pinState?: string;
}
interface WalletCardDetails {
  panReference?: string;
  tokenReferences?: string[];
}

export interface CardDetailsView {
  baseCardDetails: BaseCardDetails;
  encryptedSecureCardDetails: EncryptedSecureCardDetails;
  internalCardDetails: InternalCardDetails;
  secureCardDetails: SecureCardDetails;
  walletCardDetails: WalletCardDetails;
}
/**
 * @deprecated use CardDetailsView instead
 */
export interface Card {
  created: string;
  type: CardType;
  emboss: string;
  panLastFour: string;
  status: CardStatus;
  pinStatus: string;
  embossLine1?: string;
  embossLine2?: string;
  cardNo?: string;
  expiration?: {
    month: string;
    year: string;
  };
  cvv?: string;
  ongoingOrders?: any;
  cardToken?: string;
  visualType?: CardVisualType;
  panReferences?: string;
  tokenReferences?: Array<string>;
  pendingDelivery?: boolean;
  shippingAddress?: Address;
  _links: {
    self: Link;
    pin?: Link;
    resetLockedPin?: Link;
    freeze?: Link;
    unfreeze?: Link;
    limits?: Link;
    blockings?: Link;
    'report-broken'?: Link;
    'report-stolen'?: Link;
    block?: Link;
    unblock?: Link;
    'add-to-apple-wallet'?: Link;
    'add-to-google-wallet'?: Link;
    'provider-card'?: Link;
    partnership?: Link;
    holder?: Link & {
      name: string;
      myPicture_uri?: string;
      embedded?: {
        myPicture_uri: string;
      };
    };
    cashAccount?: Link;
  };
  _actions?: {
    editLimits?: Action;
    editBlockings?: Action;
    emit?: Action;
    terminate?: Action;
    setPin?: Action;
  };
}
